import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Trash2, Video, Building2, Star, HandHeart, FileText, Users, Activity,
  ShieldAlert, Ticket, CheckCircle2, TrendingUp, KeyRound, Lock, Bug, MessageSquareHeart,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Metric = ({ icon: Icon, label, value, tone = "brand" }) => {
  const cls = tone === "accent" ? "bg-accent/10 text-accent" : "bg-brand/10 text-brand";
  return (
    <div className="bg-white rounded-2xl border border-linen-3 p-5" data-testid={`metric-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${cls}`}><Icon className="w-4 h-4" /></span>
      <p className="font-serif text-3xl text-ink leading-none">{value}</p>
      <p className="text-xs text-ink-2 mt-1.5">{label}</p>
    </div>
  );
};

const CAT_ICON = { bug: Bug, auth: KeyRound, feedback: MessageSquareHeart };

const Admin = () => {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [mentorship, setMentorship] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [tickets, setTickets] = useState([]);
  const token = localStorage.getItem("aurea_token");
  const isAdmin = user?.role === "admin";

  const loadAll = () => {
    api.get("/admin/metrics").then(({ data }) => setMetrics(data)).catch(() => {});
    api.get("/admin/tickets").then(({ data }) => setTickets(data)).catch(() => {});
    api.get("/meetings").then(({ data }) => setMeetings(data)).catch(() => {});
    api.get("/facilities").then(({ data }) => setFacilities(data)).catch(() => {});
    api.get("/feedback").then(({ data }) => setFeedback(data)).catch(() => {});
    api.get("/mentorship").then(({ data }) => setMentorship(data)).catch(() => {});
  };
  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin]);

  const remove = async (kind, id) => {
    try { await api.delete(`/admin/${kind}/${id}`); toast.success("Removed."); loadAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const resolveTicket = async (id) => {
    try { await api.patch(`/admin/tickets/${id}/resolve`); toast.success("Marked as resolved."); loadAll(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const DelBtn = ({ kind, id }) => (
    <button onClick={() => remove(kind, id)} className="text-ink-mute hover:text-accent shrink-0" data-testid={`del-${kind}-${id}`}><Trash2 className="w-4 h-4" /></button>
  );

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto text-center py-20" data-testid="admin-denied">
        <span className="w-14 h-14 rounded-full bg-warmsoft flex items-center justify-center mx-auto mb-4"><Lock className="w-6 h-6 text-warmsoft-text" /></span>
        <h1 className="font-serif text-3xl text-ink">Restricted</h1>
        <p className="text-ink-2 mt-2">This dashboard is available to Aurea administrators only.</p>
      </div>
    );
  }

  const fmtTime = (ts) => { try { return new Date(ts).toLocaleString(); } catch { return ts; } };

  const tabs = [
    { v: "telemetry", label: "Telemetry" },
    { v: "tickets", label: `Tickets (${tickets.filter((t) => t.status === "open").length})` },
    { v: "meetings", label: `Meetings (${meetings.length})` },
    { v: "facilities", label: `Facilities (${facilities.length})` },
    { v: "feedback", label: `Reviews (${feedback.length})` },
    { v: "mentorship", label: `Mentorship (${mentorship.length})` },
  ];

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink">Admin Dashboard</h1>
        <p className="text-ink-2 mt-2">Live health, support tickets and community moderation — no private journal content is ever shown here.</p>
      </div>

      <Tabs defaultValue="telemetry" className="w-full" onValueChange={(v) => { if (v === "telemetry" || v === "tickets") loadAll(); }}>
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="bg-linen-2 rounded-full p-1 flex w-max gap-1">
            {tabs.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`} className="rounded-full px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-brand data-[state=active]:text-linen text-ink-2">{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* TELEMETRY */}
        <TabsContent value="telemetry" className="mt-6 space-y-6" data-testid="admin-telemetry">
          {!metrics ? <p className="text-sm text-ink-mute">Loading metrics…</p> : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Metric icon={Users} label="Total users" value={metrics.users.total} />
                <Metric icon={TrendingUp} label="Total visits" value={metrics.traffic.visits_total} />
                <Metric icon={Activity} label="Active (24h)" value={metrics.traffic.active_24h} />
                <Metric icon={Star} label="Plus members" value={metrics.users.plus} tone="accent" />
                <Metric icon={Users} label="Verified users" value={metrics.users.verified} />
                <Metric icon={Users} label="New (7 days)" value={metrics.users.new_7d} />
                <Metric icon={ShieldAlert} label="Crisis events" value={metrics.content.crisis_events} tone="accent" />
                <Metric icon={Ticket} label="Open tickets" value={metrics.tickets.open} tone="accent" />
              </div>

              <div className="bg-white rounded-2xl border border-linen-3 p-5">
                <p className="font-medium text-ink flex items-center gap-2 mb-1"><KeyRound className="w-4 h-4 text-brand" /> Verification / Login Health (last 24h)</p>
                <div className="flex flex-wrap gap-6 mt-3">
                  <div><p className="font-serif text-2xl text-ink">{metrics.verification.otp_success_24h}</p><p className="text-xs text-ink-2">OTP verified ✓</p></div>
                  <div><p className="font-serif text-2xl text-accent">{metrics.verification.otp_fail_24h}</p><p className="text-xs text-ink-2">OTP failed</p></div>
                  <div><p className="font-serif text-2xl text-accent">{metrics.traffic.login_fail_24h}</p><p className="text-xs text-ink-2">Login failures</p></div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-linen-3 p-5" data-testid="auth-stream">
                <p className="font-medium text-ink flex items-center gap-2 mb-3"><Activity className="w-4 h-4 text-brand" /> Auth event stream (emails masked for privacy)</p>
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {metrics.stream.length === 0 && <p className="text-sm text-ink-mute">No events yet.</p>}
                  {metrics.stream.map((e, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b border-linen last:border-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${e.ok === false ? "bg-accent" : "bg-brand"}`} />
                      <span className="text-ink font-medium w-28 shrink-0">{e.type}{e.channel ? `·${e.channel}` : ""}</span>
                      <span className="text-ink-2 flex-1 truncate">{e.email}</span>
                      <span className="text-xs text-ink-mute shrink-0">{fmtTime(e.ts)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* TICKETS */}
        <TabsContent value="tickets" className="mt-6 space-y-3" data-testid="admin-tickets">
          {tickets.length === 0 && <p className="text-sm text-ink-mute">No tickets yet.</p>}
          {tickets.map((t) => {
            const Icon = CAT_ICON[t.category] || MessageSquareHeart;
            const resolved = t.status === "resolved";
            return (
              <div key={t.id} className={`rounded-2xl border p-5 flex items-start gap-3 ${resolved ? "bg-linen-2 border-linen-3 opacity-70" : "bg-white border-linen-3"}`} data-testid="admin-ticket">
                <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${resolved ? "text-ink-mute" : "text-brand"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-ink-2"><span className="uppercase text-xs tracking-wide text-accent">{t.category}</span> · {t.email || "anonymous"} · {fmtTime(t.created_at)}</p>
                  <p className="text-ink mt-1 whitespace-pre-wrap break-words">{t.message}</p>
                </div>
                {resolved ? (
                  <span className="flex items-center gap-1 text-xs text-brand shrink-0"><CheckCircle2 className="w-4 h-4" /> Resolved</span>
                ) : (
                  <button onClick={() => resolveTicket(t.id)} className="flex items-center gap-1.5 rounded-full bg-brand text-linen text-xs px-3 py-2 hover:bg-brand-hover shrink-0" data-testid={`resolve-${t.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark resolved
                  </button>
                )}
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="meetings" className="mt-6 space-y-3">
          {meetings.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-linen-3 p-5 flex items-start gap-3" data-testid="admin-meeting">
              <Video className="w-5 h-5 text-brand mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-ink">{m.title} <span className="text-xs text-ink-mute">· {m.meeting_type}</span></p>
                <p className="text-sm text-ink-2">{m.facility} · {m.schedule}</p>
                <p className="text-xs text-accent truncate">{m.zoom_link}</p>
              </div>
              <DelBtn kind="meetings" id={m.id} />
            </div>
          ))}
          {meetings.length === 0 && <p className="text-sm text-ink-mute">No meetings.</p>}
        </TabsContent>

        <TabsContent value="facilities" className="mt-6 space-y-3">
          {facilities.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl border border-linen-3 p-5 flex items-start gap-3" data-testid="admin-facility">
              <Building2 className="w-5 h-5 text-brand mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-ink">{f.name}</p>
                <p className="text-sm text-ink-2">{f.location} · {f.contact}</p>
                {f.files?.map((doc) => (
                  <a key={doc.id} href={`${BACKEND}/api/files/${doc.path}?auth=${token}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-accent hover:underline mt-1"><FileText className="w-3.5 h-3.5" />{doc.name}</a>
                ))}
              </div>
              <DelBtn kind="facilities" id={f.id} />
            </div>
          ))}
          {facilities.length === 0 && <p className="text-sm text-ink-mute">No facilities.</p>}
        </TabsContent>

        <TabsContent value="feedback" className="mt-6 space-y-3">
          {feedback.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl border border-linen-3 p-5 flex items-start gap-3" data-testid="admin-feedback">
              <Star className="w-5 h-5 text-accent mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-ink">{f.name} · {f.rating}★</p>
                <p className="text-sm text-ink-2">{f.message}</p>
              </div>
              <DelBtn kind="feedback" id={f.id} />
            </div>
          ))}
          {feedback.length === 0 && <p className="text-sm text-ink-mute">No reviews.</p>}
        </TabsContent>

        <TabsContent value="mentorship" className="mt-6 space-y-3">
          {mentorship.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-linen-3 p-5 flex items-start gap-3" data-testid="admin-mentorship">
              <HandHeart className="w-5 h-5 text-brand mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-ink">{m.name} <span className="text-xs text-ink-mute">· {m.role}</span></p>
                <p className="text-sm text-ink-2">{m.focus}{m.note ? ` — ${m.note}` : ""}</p>
              </div>
              <DelBtn kind="mentorship" id={m.id} />
            </div>
          ))}
          {mentorship.length === 0 && <p className="text-sm text-ink-mute">No mentorship entries.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;
